import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import GamepadIcon from './GamepadIcon';

const Header = ({ gameData, hideSearch = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const menuRef = useRef(null);
  
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
      setShowResults(false);
    }
  };
  
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    if (value.trim().length > 1) {
      performSearch(value);
      setShowResults(true);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  };
  
  const performSearch = (query) => {
    if (!gameData) return;
    
    const results = [];
    let count = 0;
    
    // Search through all categories and games
    Object.keys(gameData).forEach(category => {
      if (gameData[category] && Array.isArray(gameData[category]) && count < 5) {
        const matchingGames = gameData[category].filter(game => {
          // Search in name, description, and tags
          const nameMatch = game.name && game.name.toLowerCase().includes(query.toLowerCase());
          return nameMatch;
        }).slice(0, 5 - count);
        
        // Add category info to each matching game
        matchingGames.forEach(game => {
          if (count < 5) {
            results.push({
              ...game,
              category
            });
            count++;
          }
        });
      }
    });
    
    setSearchResults(results);
  };
  
  const handleResultClick = (category, id) => {
    navigate(`/game/${category}/${id}`);
    setSearchTerm('');
    setShowResults(false);
  };
  
  // Load categories from gameData
  useEffect(() => {
    if (gameData) {
      const categoryNames = Object.keys(gameData);
      setCategories(categoryNames);
    }
  }, [gameData]);

  // Toggle menu
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  // Handle menu item click
  const handleCategoryClick = (category) => {
    navigate(`/category/${category}`);
    setMenuOpen(false);
  };

  // Close search results and menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  return (
    <HeaderContainer>
      <MenuWrapper ref={menuRef}>
        <MenuButton onClick={toggleMenu}>☰</MenuButton>
        {menuOpen && (
          <SideMenu>
            <MenuHeader>Categories</MenuHeader>
            <CategoryList>
              {categories.map(category => (
                <CategoryItem key={category} onClick={() => handleCategoryClick(category)}>
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </CategoryItem>
              ))}
            </CategoryList>
          </SideMenu>
        )}
      </MenuWrapper>
      
      <Logo>
        <Link to="/">
          <LogoWrapper>
            <GamepadIcon size={32} color="#45b7d8" />
            <LogoText>Game Hub</LogoText>
          </LogoWrapper>
        </Link>
      </Logo>
      
      {!hideSearch && (
        <SearchContainer onSubmit={handleSearch} ref={searchRef}>
        <SearchInputWrapper>
          <SearchInput 
            type="text" 
            placeholder="Search games..." 
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <SearchButton type="submit">🔍</SearchButton>
          
          {showResults && searchResults.length > 0 && (
            <SearchResultsDropdown>
              {searchResults.map(game => (
                <SearchResultItem 
                  key={`${game.category}-${game.id}`}
                  onClick={() => handleResultClick(game.category, game.id)}
                >
                  <SearchResultThumb src={game.thumb300x300 || game.thumb512x384} alt={game.name} />
                  <SearchResultInfo>
                    <SearchResultName>{game.name}</SearchResultName>
                    <SearchResultCategory>{game.category}</SearchResultCategory>
                  </SearchResultInfo>
                </SearchResultItem>
              ))}
              <ViewAllResults onClick={() => {
                if (searchTerm.trim()) {
                  navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
                  setShowResults(false);
                }
              }}>
                View all results
              </ViewAllResults>
            </SearchResultsDropdown>
          )}
        </SearchInputWrapper>
      </SearchContainer>
      )}
    </HeaderContainer>
  );
};

const HeaderContainer = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  border-bottom: 1px solid #eaeaea;
`;

const Logo = styled.div`
  a {
    text-decoration: none;
  }
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const LogoText = styled.h1`
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin: 0;
  background: linear-gradient(to right, #ff6b6b, #4ecdc4, #45b7d8);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SearchContainer = styled.form`
  display: flex;
  flex: 1;
  max-width: 500px;
  margin: 0 20px;
  position: relative;
`;

const SearchInputWrapper = styled.div`
  display: flex;
  flex: 1;
  position: relative;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px 0 0 4px;
  font-size: 14px;
  outline: none;
`;

const SearchButton = styled.button`
  background: #45b7d8;
  border: none;
  padding: 0 15px;
  border-radius: 0 4px 4px 0;
  color: white;
  cursor: pointer;
`;

const MenuWrapper = styled.div`
  position: relative;
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
`;

const SideMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 220px;
  background: white;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  margin-top: 10px;
  overflow: hidden;
`;

const MenuHeader = styled.div`
  background: #45b7d8;
  color: white;
  padding: 15px;
  font-weight: bold;
  font-size: 16px;
`;

const CategoryList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 400px;
  overflow-y: auto;
`;

const CategoryItem = styled.li`
  padding: 12px 15px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: #f5f5f5;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const SearchResultsDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  max-height: 400px;
  overflow-y: auto;
  margin-top: 2px;
`;

const SearchResultItem = styled.div`
  display: flex;
  padding: 10px;
  cursor: pointer;
  border-bottom: 1px solid #eee;
  
  &:hover {
    background: #f5f5f5;
  }
`;

const SearchResultThumb = styled.img`
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 4px;
  margin-right: 10px;
`;

const SearchResultInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const SearchResultName = styled.div`
  font-weight: bold;
  margin-bottom: 4px;
`;

const SearchResultCategory = styled.div`
  font-size: 12px;
  color: #45b7d8;
  text-transform: capitalize;
`;

const ViewAllResults = styled.div`
  text-align: center;
  padding: 10px;
  background: #f5f5f5;
  color: #45b7d8;
  font-weight: bold;
  cursor: pointer;
  
  &:hover {
    background: #e5e5e5;
  }
`;

export default Header;
